import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";

interface SubItem {
  label: string;
  to: string;
}

interface NavItem {
  label: string;
  to: string;
  children?: SubItem[];
}

const navItems: NavItem[] = [
  { label: "首页", to: "/" },
  {
    label: "电影",
    to: "/category/电影",
    children: [
      { label: "全部电影", to: "/category/电影" },
      { label: "科幻", to: "/category/电影/科幻" },
      { label: "动作", to: "/category/电影/动作" },
      { label: "科幻冒险", to: "/category/电影/冒险" },
      { label: "爱情", to: "/category/电影/爱情" },
      { label: "悬疑", to: "/category/电影/悬疑" },
      { label: "剧情", to: "/category/电影/剧情" },
      { label: "奇幻", to: "/category/电影/奇幻" },
    ],
  },
  {
    label: "电视剧",
    to: "/category/电视剧",
    children: [
      { label: "全部电视剧", to: "/category/电视剧" },
      { label: "古装", to: "/category/电视剧/古装" },
      { label: "都市", to: "/category/电视剧/都市" },
      { label: "悬疑", to: "/category/电视剧/悬疑" },
      { label: "爱情", to: "/category/电视剧/爱情" },
      { label: "武侠", to: "/category/电视剧/武侠" },
      { label: "科幻", to: "/category/电视剧/科幻" },
    ],
  },
  { label: "资讯", to: "/news" },
  { label: "关于", to: "/about" },
];

const linkBase =
  "relative text-sm text-background-100/90 transition-colors hover:text-accent-400";

export default function Navbar() {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSub, setMobileSub] = useState<string | null>(null);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileOpen]);

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    if (!q) return;
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setMobileOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 z-50 w-full transition-colors duration-300 ${
        scrolled ? "bg-primary-950/95 backdrop-blur" : "bg-gradient-to-b from-black/60 to-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-site items-center gap-6 px-4 md:h-[68px] md:px-6">
        {/* Logo */}
        <Link to="/" className="flex shrink-0 items-center gap-2" aria-label="秋霞影视首页">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500 text-primary-950">
            <i className="ri-clapperboard-fill text-xl" aria-hidden="true"></i>
          </span>
          <span className="font-heading text-xl font-700 tracking-wide text-background-50">
            秋霞影视
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-1 md:flex" aria-label="主导航">
          {navItems.map((item) => (
            <div key={item.label} className="group relative">
              <NavLink
                to={item.to}
                className={({ isActive }) =>
                  `${linkBase} flex items-center gap-1 rounded-md px-3 py-2 whitespace-nowrap ${
                    isActive ? "text-accent-400" : ""
                  }`
                }
              >
                {item.label}
                {item.children && (
                  <i className="ri-arrow-down-s-line text-base transition-transform group-hover:rotate-180" aria-hidden="true"></i>
                )}
              </NavLink>
              {item.children && (
                <div className="invisible absolute left-0 top-full w-48 translate-y-2 rounded-lg border border-background-200 bg-background-50 p-2 opacity-0 shadow-none transition-all duration-200 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                  {item.children.map((sub) => (
                    <Link
                      key={sub.to}
                      to={sub.to}
                      className="block rounded-md px-3 py-2 text-sm text-foreground-700 transition-colors hover:bg-primary-50 hover:text-primary-700"
                    >
                      {sub.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Desktop search */}
        <form
          onSubmit={submitSearch}
          className="ml-auto hidden items-center gap-2 rounded-full border border-background-100/25 bg-background-50/10 px-3 py-1.5 md:flex"
          role="search"
        >
          <span className="flex h-4 w-4 items-center justify-center text-background-100/80">
            <i className="ri-search-line text-base" aria-hidden="true"></i>
          </span>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            type="text"
            name="q"
            placeholder="搜索影视名称"
            aria-label="搜索影视名称"
            className="w-32 bg-transparent text-sm text-background-50 placeholder:text-background-100/60 focus:outline-none lg:w-44"
          />
        </form>

        <Link
          to="/category/电影"
          className="ml-auto hidden rounded-full bg-accent-500 px-4 py-2 text-sm font-500 text-primary-950 transition-colors hover:bg-accent-400 md:ml-0 md:block whitespace-nowrap"
        >
          热门推荐
        </Link>

        {/* Mobile toggle */}
        <button
          type="button"
          onClick={() => setMobileOpen((v) => !v)}
          className="ml-auto flex h-9 w-9 items-center justify-center rounded-md text-background-50 md:hidden cursor-pointer"
          aria-label={mobileOpen ? "关闭菜单" : "打开菜单"}
          aria-expanded={mobileOpen}
        >
          <i className={mobileOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} aria-hidden="true"></i>
        </button>
      </div>

      {/* Mobile panel */}
      {mobileOpen && (
        <div className="max-h-[calc(100vh-64px)] overflow-y-auto border-t border-background-100/10 bg-primary-950/98 px-4 pb-6 pt-4 md:hidden">
          <form
            onSubmit={submitSearch}
            className="mb-4 flex items-center gap-2 rounded-full border border-background-100/20 bg-background-50/10 px-4 py-2.5"
            role="search"
          >
            <span className="flex h-4 w-4 items-center justify-center text-background-100/80">
              <i className="ri-search-line text-base" aria-hidden="true"></i>
            </span>
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="text"
              name="q"
              placeholder="搜索影视名称"
              aria-label="搜索影视名称"
              className="w-full bg-transparent text-sm text-background-50 placeholder:text-background-100/60 focus:outline-none"
            />
          </form>

          <nav className="flex flex-col" aria-label="移动端导航">
            {navItems.map((item) => (
              <div key={item.label} className="border-b border-background-100/10">
                <div className="flex items-center justify-between">
                  <Link
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    className="flex-1 py-3.5 text-base text-background-50"
                  >
                    {item.label}
                  </Link>
                  {item.children && (
                    <button
                      type="button"
                      onClick={() => setMobileSub((v) => (v === item.label ? null : item.label))}
                      className="flex h-9 w-9 items-center justify-center text-background-100/80 cursor-pointer"
                      aria-label={`展开${item.label}子菜单`}
                    >
                      <i
                        className={
                          mobileSub === item.label
                            ? "ri-arrow-up-s-line text-xl"
                            : "ri-arrow-down-s-line text-xl"
                        }
                        aria-hidden="true"
                      ></i>
                    </button>
                  )}
                </div>
                {item.children && mobileSub === item.label && (
                  <div className="grid grid-cols-3 gap-2 pb-3">
                    {item.children.map((sub) => (
                      <Link
                        key={sub.to}
                        to={sub.to}
                        onClick={() => setMobileOpen(false)}
                        className="rounded-md bg-background-50/10 px-2 py-2 text-center text-sm text-background-100"
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}